import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { defaultTabParam } from './paths'

/**
 * Hook for managing active tab state via URL query parameter.
 * Compatible with antd Tabs component.
 *
 * @param defaultValue - Default tab key to use if none is in URL
 * @param slugMapping - Optional mapping for converting between internal keys and URL slugs
 * @param paramName - Tab parameter name (default is value of defaultTabParam)
 * @returns [activeKey, setActiveKey] - Similar to useState() structure
 */

export function useTabKeyState<T extends string>(
  defaultValue: T,
  slugMapping?: Record<T, string>,
  paramName = defaultTabParam
): [T, (key: string) => void] {
  const location = useLocation()
  const history = useHistory()
  const isFirstRender = useRef(true)

  const reverseMapping = useMemo(() => {
    if (!slugMapping) return {}

    const reversed: Record<string, T> = {}
    Object.entries<string>(slugMapping).forEach(([key, value]) => {
      reversed[value] = key as T
    })

    return reversed
  }, [slugMapping])

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const activeKey = useMemo(() => {
    const slugValue = queryParams.get(paramName) || defaultValue
    if (reverseMapping && slugValue in reverseMapping) {
      return reverseMapping[slugValue]
    }

    return slugValue as T
  }, [queryParams, paramName, defaultValue, reverseMapping])

  const setActiveKey = useCallback(
    (key: string) => {
      // TODO: remove this line when the new auth is deployed */
      queryParams.delete('t')

      const slugValue =
        slugMapping && key in slugMapping ? slugMapping[key as T] : key

      queryParams.set(paramName, slugValue)
      history.push({
        pathname: location.pathname,
        search: queryParams.toString(),
      })
    },
    [history, location.pathname, paramName, queryParams, slugMapping]
  )

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false

      if (!queryParams.get(paramName)) {
        const slugDefault = slugMapping
          ? slugMapping[defaultValue]
          : defaultValue
        queryParams.set(paramName, slugDefault)

        history.replace({
          pathname: location.pathname,
          search: queryParams.toString(),
        })
      }
    }
  }, [
    defaultValue,
    history,
    location.pathname,
    paramName,
    queryParams,
    slugMapping,
  ])

  return [activeKey, setActiveKey]
}
